import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ScrollText, UserPlus, BadgePlus, Building2 } from "lucide-react";

const TYPE_META = {
  user_created:    { Icon: UserPlus,    label: "USER",    color: "#F9D71C", fg: "#000" },
  cert_created:    { Icon: BadgePlus,   label: "CERT",    color: "#34C759", fg: "#fff" },
  company_created: { Icon: Building2,   label: "COMPANY", color: "#3B82F6", fg: "#fff" },
};

export default function OwnerLogs() {
  const [feed, setFeed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    api.get("/owner/logs")
      .then((r) => setFeed(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);

  const rows = filter === "all" ? feed : feed.filter((f) => f.type === filter);

  return (
    <div className="p-4 md:p-10 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="font-heading text-3xl md:text-4xl">ACTIVITY LOG</h1>
        <p className="text-sm text-muted-foreground mt-2">Recent platform-wide events.</p>
      </div>

      <div className="flex gap-2 mb-5 flex-wrap" data-testid="log-filters">
        {[
          { key: "all", label: "All" },
          { key: "user_created", label: "Users" },
          { key: "cert_created", label: "Certifications" },
          { key: "company_created", label: "Companies" },
        ].map((f) => (
          <button
            key={f.key}
            data-testid={`filter-${f.key}`}
            onClick={() => setFilter(f.key)}
            className={`h-10 px-4 text-xs font-bold uppercase tracking-wider border-2 transition-colors ${
              filter === f.key
                ? "bg-safety-yellow text-black border-black"
                : "border-foreground/30 dark:border-safety-yellow/40 hover:border-safety-yellow"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="p-10 text-center text-muted-foreground font-bold uppercase tracking-wider">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="p-10 text-center text-muted-foreground font-bold uppercase tracking-wider border-2 border-foreground/30 dark:border-safety-yellow/30 flex flex-col items-center gap-2">
          <ScrollText className="w-10 h-10 text-muted-foreground/50" strokeWidth={2} />
          No events yet.
        </div>
      ) : (
        <ul className="border-2 border-foreground dark:border-safety-yellow bg-card" data-testid="log-feed">
          {rows.map((r) => {
            const meta = TYPE_META[r.type] || TYPE_META.user_created;
            return (
              <li key={`${r.type}|${r.at || ""}|${r.actor || ""}`} className="flex items-center gap-3 px-4 py-3 border-b-2 border-foreground/10 dark:border-safety-yellow/20 last:border-b-0">
                <span
                  className="inline-flex items-center justify-center h-9 w-12 border-2 text-[10px] font-black uppercase tracking-wider shrink-0"
                  style={{ backgroundColor: meta.color, borderColor: meta.color, color: meta.fg }}
                >
                  {meta.label}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold truncate">{r.actor || "—"}</div>
                  <div className="text-xs text-muted-foreground truncate">{r.summary}</div>
                </div>
                <div className="hidden sm:block text-[10px] font-bold uppercase tracking-wider text-muted-foreground shrink-0">
                  {r.at ? new Date(r.at).toLocaleString() : "—"}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
